import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router";

import { Alert, Button, Card, Modal, SegmentedControl, Tabs, Text } from "@mantine/core";

import ShiftApi from "../../../data/api/shiftsApi";

import { IState } from "../../../data/reducers/state";
import { IPublicProcessedShift } from "../../../types/shifts/PublicShift";
import { toDateTime } from "../../../util/date+helper";
import { TableData } from "../../common/TableData";
import { CenteredLoader } from "../../common/loader";
import LoginForm from "../../login/LoginForm";
import PublicRosterGrid from "./PublicRosterGrid";
import PublicRosterModern from "./PublicRosterModern";

interface IPublicSlotShift {
  name: string;
  venues: string[];
  slots: { [venue: string]: (any | null)[] };
  start: string;
}

interface IPublishedRosterProps {
  isUnauthenticated: boolean;
}

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const PHONE_REGEX =
  /^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

const PublishedRoster: React.FunctionComponent<IPublishedRosterProps> = (
  props,
) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isAuthing, setIsAuthing] = React.useState<boolean>(false);
  const [shift, setShift] = React.useState<IPublicProcessedShift | null>();
  const [error, setError] = React.useState<string | null>();
  const [tab, setTab] = React.useState<string | null>();

  const { sessionToken } = useParams();

  const query = useQuery();
  const [viewMode, setViewMode] = React.useState<"classic" | "modern">(
    query.get("layout") === "modern" ? "modern" : "classic",
  );

  let interval = 45; // seconds
  const _queryInterval = query.get("interval");
  if (_queryInterval) {
    const parsedInterval = parseInt(_queryInterval);
    if (!isNaN(parsedInterval)) {
      interval = parsedInterval;
    }
  }

  const loop = (timeout?: number) => {
    let _timeout = Math.min(45, interval);
    if (timeout) {
      _timeout = timeout;
    }
    setTimeout(getShift, _timeout * 1000, false);
  };

  const getShift = (shouldShowLoadingIndiciator = true) => {
    if (shouldShowLoadingIndiciator) {
      setShift(null);
      setIsLoading(true);
      setError(null);
    }

    ShiftApi.getPublishedShift(sessionToken)
      .then((shift) => {
        setShift(shift);
        setIsLoading(false);
        setError(null);
        setTab(tab && tab in shift.venues ? tab : Object.keys(shift.venues)[0]);

        loop();
      })
      .catch((er) => {
        setIsLoading(false);
        setError(`${er.message} (trying again in 10 seconds)`);
        // 10 seconds for error
        loop(10);
      });
  };

  React.useEffect(() => {
    if (!sessionToken) {
      return setError("Invalid session token for viewing public shift");
    }
    getShift();
  }, [sessionToken]);

  const venueKeys = shift ? Object.keys(shift.venues) : [];
  const activeTab =
    tab && shift && shift.venues[tab] ? tab : venueKeys[0] ?? null;
  const selectedVenue =
    activeTab && shift && shift.venues[activeTab]
      ? shift.venues[activeTab]
      : null;
  const supervisors =
    activeTab && shift && shift.supervisors?.[activeTab]
      ? shift.supervisors[activeTab]
      : [];
  const cacheLastUpdated =
    shift && shift.cacheLastUpdated && DateTime.isDateTime(shift.cacheLastUpdated)
      ? shift.cacheLastUpdated.toLocal()
      : null;
  const cacheLastUpdatedLabel =
    shift &&
    (cacheLastUpdated
      ? cacheLastUpdated.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
      : "Fresh");

  const classicView =
    shift && activeTab && selectedVenue ? (
      <>
        <div style={{ padding: "40px 40px 10px 40px" }}>
          <h3>{shift.name}</h3>
          <h5>{toDateTime(shift.start).toLocaleString(DateTime.DATE_FULL)}</h5>
        </div>

        <Tabs
          value={activeTab}
          onChange={setTab}
          style={{
            marginLeft: "40px",
            marginRight: "10px",
            marginBottom: "10px",
          }}
        >
          <Tabs.List>
            {venueKeys.map((v) => (
              <Tabs.Tab key={v} value={v}>
                {shift.venues[v].name ?? v}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        {supervisors.length > 0 && (
          <div style={{ maxWidth: "500px", padding: "10px 40px 0px 40px" }}>
            <p>
              <b>Supervisors:</b>
            </p>
            <TableData
              data={supervisors.map((s) => [
                <b key={`name-${s.name}`}>{s.name}</b>,
                `${toDateTime(s.start)?.toFormat("hh:mm a")} - ${toDateTime(
                  s.finish,
                )?.toFormat("hh:mm a")}`,
                s.mobile ? (
                  PHONE_REGEX.test(s.mobile) ? (
                    <a href={`tel:${s.mobile}`} key={`mobile-${s.name}`}>
                      {s.mobile}
                    </a>
                  ) : (
                    s.mobile
                  )
                ) : (
                  "Login to see supervisor details"
                ),
              ])}
            />

          </div>
        )}

        <PublicRosterGrid
          slots={shift.slots[activeTab]}
          courts={selectedVenue.courts}
        />
      </>
    ) : null;

  const modernView =
    shift && activeTab ? (
      <PublicRosterModern
        shift={shift}
        activeVenueKey={activeTab}
        onVenueChange={(value) => setTab(value)}
      />
    ) : null;

  return (
    <Card style={{ margin: "0px" }}>
      {isLoading && !shift && (
        <>
          <em>Getting shift details</em>
          <CenteredLoader />
        </>
      )}
      {error && (
        <Alert color="red">
          <p>
            There was an error while getting this shift:
            {error}
          </p>
        </Alert>
      )}
      {shift && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
              padding: "30px 40px 10px 40px",
              flexWrap: "wrap",
            }}
          >
            <SegmentedControl
              value={viewMode}
              onChange={(value) => setViewMode(value as "classic" | "modern")}
              data={[
                { label: "Classic", value: "classic" },
                { label: "Modern", value: "modern" },
              ]}
            />
            {props.isUnauthenticated && (
              <Button id="public-login-button" onClick={() => setIsAuthing(true)}>
                Login
              </Button>
            )}
          </div>

          {isAuthing && (
            <Modal
              size="sm"
              opened={isAuthing}
              onClose={() => setIsAuthing(false)}
              title="Login"
            >
              <LoginForm callback={() => setIsAuthing(false)} />
            </Modal>
          )}

          {viewMode === "modern" ? modernView : classicView}
        </>
      )}
      {cacheLastUpdatedLabel && (
        <div style={{ marginLeft: "auto", fontSize: "0.9rem", textAlign: "right" }}>
          <Text>
            Last updated: {cacheLastUpdatedLabel}
          </Text>
        </div>
      )}
    </Card>
  );
};

function mapState2Props(
  state: IState,
  props: IPublishedRosterProps,
): Partial<IPublishedRosterProps> {
  return {
    isUnauthenticated: !state.auth.loading && !state.auth.hasAuthenticated,
  };
}

export default connect(mapState2Props)(PublishedRoster);
